import PropTypes from 'prop-types'
import { AnalyticsConsumer } from '@opallabs/quanta'
import Conditional from '../../Conditional'
import { renderIcon, kuiperClassNames, stopEvent } from '../../utils'
import Input from '../Input'

import './styles.scss'

const keyDown = (onChange, value, disabled, e) => {
  if (disabled) {
    return
  }

  if (e.keyCode === 32) {
    onChange(!value)
  }
}

function getCheck(value) {
  if (value === 'some') {
    return renderIcon('minus', { ariaMode: 'presentational' })
  }

  return renderIcon('checkmark', { ariaMode: 'presentational' })
}

/**
 * An accessible checkbox input.
 *
 * @note The `value` for this component must be managed from the containing
 *   component. This component does not maintain `value` in its state.
 */
export default class CheckBox extends Input {
  static propTypes = {
    ariaDescribedBy: PropTypes.string,
    disabled: PropTypes.bool,
    displayType: PropTypes.oneOf(['default', 'filechecker']),
    elementRef: PropTypes.object,
    hideLabelText: PropTypes.bool,
    label: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func,
    style: PropTypes.object,
    tabIndex: PropTypes.number,
    theme: PropTypes.string,
    ariaLabel: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  }

  static defaultProps = {
    ...Input.defaultProps,
    hideLabelText: false,
    value: false,
    tabIndex: 0,
    theme: 'light',
    displayType: 'default',
  }

  changer = (track, e) => {
    if (this.props.disabled) {
      return
    }

    track('checkbox', {
      value: !this.props.value, // flipped to send value user is changing to
      label: this.props.label,
    })

    stopEvent(e)
    this.props.onChange(!this.value)
  }

  render() {
    const {
      ariaDescribedBy,
      disabled,
      elementRef,
      hideLabelText,
      label,
      onChange,
      displayType,
      ariaLabel,
      style,
      tabIndex,
      theme,
      value,
    } = this.props
    const { focused } = this.state
    const checked = value !== 'none' && value !== false

    let labelClass = ''
    if (hideLabelText) {
      labelClass = 'sr-only'
    }

    return (
      <AnalyticsConsumer>
        {({ track }) => (
          /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
          <div
            className={kuiperClassNames(
              'input',
              'checkbox',
              displayType,
              theme,
              {
                checked,
                focused,
                disabled,
              }
            )}
            style={style}
          >
            <div
              aria-checked={value}
              aria-describedby={ariaDescribedBy}
              aria-disabled={disabled}
              aria-label={ariaLabel}
              className={kuiperClassNames('checkbox-frame')}
              aria-labelledby={this.state.inputId}
              onBlur={this.inputBlur()}
              onClick={(e) => {
                this.changer(track, e)
              }}
              onFocus={this.inputFocus}
              onKeyDown={keyDown.bind(this, onChange, value, disabled)}
              ref={elementRef}
              role="checkbox"
              tabIndex={disabled ? -1 : tabIndex}
            >
              {getCheck(value)}
            </div>
            <Conditional condition={label}>
              <label
                className={labelClass}
                id={this.state.inputId}
                onClick={(e) => {
                  this.changer(track, e)
                }}
              >
                {label}
              </label>
            </Conditional>
          </div>
        )}
      </AnalyticsConsumer>
    )
  }
}
